/**
 * WEEK chips (W1…Wn) colored by status, plus the 完了 / 着手中 / 未着手 legend below them.
 */

import { TSubjectCurriculumDto, TSubjectStatusDto } from "@/api/services/main/learning-progress/dtos/get-learner-curriculum-progress.dto";
import { WEEK_STATUS_COLORS } from "@/features/learning-progress/components/learner-detail/curriculum-progress/CurriculumProgressBar";
import { TCurriculumGroupCounts, getWeekLabel } from "@/features/learning-progress/utils/curriculum-groups";
import { black, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Box, Stack, Typography } from "@mui/material";
import { FC, memo } from "react";

type TWeekChipItem = Pick<TSubjectCurriculumDto, "id" | "name" | "status">;

type TWeekStatusChipsProps = {
  subjects: TWeekChipItem[];
  counts: TCurriculumGroupCounts;
};

/** Chip fill + label color per status — 未着手 keeps a light chip with dark text */
const CHIP_STYLES: Record<TSubjectStatusDto, { bgcolor: string; color: string }> = {
  completed: { bgcolor: WEEK_STATUS_COLORS.completed, color: text.white },
  in_progress: { bgcolor: WEEK_STATUS_COLORS.inProgress, color: text.white },
  not_started: { bgcolor: WEEK_STATUS_COLORS.notStarted, color: black[30] },
};

const CHIP_WIDTH = 30;
const CHIP_HEIGHT = 15;
const CHIP_FONT_SIZE = 10;
const LEGEND_DOT_SIZE = 12;
const LEGEND_FONT_SIZE = 10;

export const WeekStatusChips: FC<TWeekStatusChipsProps> = memo(({ subjects, counts }) => (
  <Stack gap={0.5}>
    {/* Chip row — wraps once the WEEK count outgrows the block width */}
    <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={0.5}>
      {subjects.map((subject, index) => (
        <Box
          key={subject.id}
          sx={{
            width: CHIP_WIDTH,
            height: CHIP_HEIGHT,
            borderRadius: rounded.xs,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...CHIP_STYLES[subject.status],
          }}
        >
          {/* Fixed-height chip — label sits on a matching line box, so no typography preset here */}
          <Typography sx={{ fontSize: CHIP_FONT_SIZE, lineHeight: `${CHIP_HEIGHT}px`, fontWeight: 700, color: "inherit" }}>
            {getWeekLabel(subject, index)}
          </Typography>
        </Box>
      ))}
    </Stack>

    {/* Legend */}
    <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1}>
      <LegendItem color={WEEK_STATUS_COLORS.completed} label={`完了: ${counts.completed}`} />
      <LegendItem color={WEEK_STATUS_COLORS.inProgress} label={`着手中: ${counts.inProgress}`} />
      <LegendItem color={WEEK_STATUS_COLORS.notStarted} label={`未着手: ${counts.notStarted}`} />
    </Stack>
  </Stack>
));
WeekStatusChips.displayName = "WeekStatusChips";

/** Colored square + caption used under the chips */
const LegendItem: FC<{ color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <Box sx={{ width: LEGEND_DOT_SIZE, height: LEGEND_DOT_SIZE, borderRadius: rounded.xs, bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: LEGEND_FONT_SIZE, color: text.secondary, whiteSpace: "nowrap" }}>{label}</Typography>
  </Stack>
);
